/**
 * Course Manifest Generator
 * Generates CourseManifest for export packages.
 */

import type {
  CourseManifest,
  ExportConfig,
  ExportSource,
  ExportedCourse,
  LessonVersionInfo,
} from '@course-authoring/shared-types';
import { generateContentHash } from './contentHash';

/**
 * Input parameters for generating a course manifest.
 */
export interface GenerateManifestInput {
  /** The exported course content (after overlay resolution) */
  course: ExportedCourse;

  /** Where the export reads its content from */
  source: ExportSource;

  /**
   * Per-lesson published-version metadata. Required for `published` exports;
   * pass `[]` for `draft`.
   */
  lessonVersions: LessonVersionInfo[];

  /** Export configuration */
  config: ExportConfig;

  /** User ID who triggered the export */
  exportedBy: string;
}

/**
 * Generates a CourseManifest for an export package.
 */
export async function generateCourseManifest(
  input: GenerateManifestInput
): Promise<CourseManifest> {
  const { course, source, lessonVersions, config, exportedBy } = input;

  const contentHash = await generateContentHash(course);

  const manifest: CourseManifest = {
    courseId: course.id,
    title: course.title,
    description: course.description,
    defaultLocale: course.defaultLocale,
    availableLocales: course.availableLocales,
    source,
    exportedAt: new Date().toISOString(),
    exportedBy,
    contentHash,
    lessonVersions,
    config: {
      enableOfflineMode: config.enableOfflineMode,
      ...(config.assetBaseUrl && { assetBaseUrl: config.assetBaseUrl }),
    },
  };

  if (course.completionCertificateEnabled !== undefined) {
    manifest.completionCertificateEnabled = course.completionCertificateEnabled;
  }
  if (course.certificateMessage) {
    manifest.certificateMessage = course.certificateMessage;
  }
  if (course.completedMessage) {
    manifest.completedMessage = course.completedMessage;
  }

  return manifest;
}
